// ./src/components/features/AssignContentBlockAsEmergencyToDisplayModal/AssignContentBlockAsEmergencyToDisplayModal.tsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Stack,
  Text,
  Group,
  Checkbox,
  ScrollArea,
  Alert,
  Box,
  Divider,
} from "@mantine/core";
import { AppModal, AppIcon, LoadingIndicator } from "@/components/generic";
import { ContentTypeBadge } from "@/components/features";
import { ContentBlock } from "@/utils/api";
import { useDisplays } from "@/hooks";
import { formatDate, formatTimeRange, formatDaysOfWeek } from "@/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export interface AssignContentBlockAsEmergencyToDisplayModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Content block to assign */
  contentBlock: ContentBlock;

  /** Optional venue ID to filter displays */
  venueId?: string;
}

interface DisplayOperationState {
  displayId: string;
  loading: boolean;
  error: string | null;
}

export const AssignContentBlockAsEmergencyToDisplayModal: React.FC<
  AssignContentBlockAsEmergencyToDisplayModalProps
> = ({ opened, onClose, contentBlock, venueId }) => {
  // State for tracking operations per display
  const [operations, setOperations] = useState<
    Record<string, DisplayOperationState>
  >({});

  // Hook for all displays
  const {
    displays,
    loading,
    list: listDisplays,
    update: updateDisplay,
  } = useDisplays({
    autoLoad: false,
    venueId,
  });

  // Load displays when modal opens
  useEffect(() => {
    if (opened) {
      listDisplays(
        venueId ? { venueId: [venueId] } : {},
        { page: 1, pageSize: 1000 },
        undefined,
        true
      );
    }
  }, [opened, venueId, listDisplays]);

  // Create a set of assigned display IDs for quick lookup
  const assignedDisplayIds = useMemo(() => {
    return new Set(
      displays
        .filter(
          (display) => display.emergencyContentBlockId === contentBlock.id
        )
        .map((display) => display.id)
    );
  }, [displays, contentBlock.id]);

  // Reset operations state when modal closes
  useEffect(() => {
    if (!opened) {
      setOperations({});
    }
  }, [opened]);

  // Handle checkbox change
  const handleCheckboxChange = async (displayId: string, checked: boolean) => {
    // Set loading state for this display
    setOperations((prev) => ({
      ...prev,
      [displayId]: {
        displayId,
        loading: true,
        error: null,
      },
    }));

    try {
      const result = await updateDisplay(displayId, {
        emergencyContentBlockId: checked ? contentBlock.id : null,
      });

      if (result) {
        // Success - clear operation state
        setOperations((prev) => {
          const newOps = { ...prev };
          delete newOps[displayId];
          return newOps;
        });
      } else {
        // Error - show error message
        setOperations((prev) => ({
          ...prev,
          [displayId]: {
            displayId,
            loading: false,
            error: checked
              ? "Failed to assign emergency content block"
              : "Failed to remove emergency content block",
          },
        }));
      }
    } catch (error) {
      setOperations((prev) => ({
        ...prev,
        [displayId]: {
          displayId,
          loading: false,
          error: error instanceof Error ? error.message : "Operation failed",
        },
      }));
    }
  };

  // Clear error for a specific display
  const clearDisplayError = (displayId: string) => {
    setOperations((prev) => {
      const newOps = { ...prev };
      delete newOps[displayId];
      return newOps;
    });
  };

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title="Assign Emergency Content Block to Displays"
      size="lg"
      actionType="close"
    >
      <Stack gap="md">
        {/* Content Block Info */}
        <Box>
          <Stack gap="xs">
            <Group gap="xs" align="center">
              <Text size="sm" fw={600}>
                {contentBlock.name || "Untitled Content Block"}
              </Text>
              <ContentTypeBadge type={contentBlock.contentType as any} />
            </Group>

            {contentBlock.description && (
              <Text size="sm" c="dimmed">
                {contentBlock.description}
              </Text>
            )}

            {/* Schedule Info */}
            {(contentBlock.startDatetime ||
              contentBlock.endDatetime ||
              contentBlock.startTime ||
              contentBlock.endTime ||
              contentBlock.recurrencePattern) && (
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>
                  Schedule:
                </Text>
                {(contentBlock.startDatetime || contentBlock.endDatetime) && (
                  <Text size="xs" c="dimmed">
                    {formatDate(contentBlock.startDatetime)} -{" "}
                    {formatDate(contentBlock.endDatetime)}
                  </Text>
                )}
                {(contentBlock.startTime || contentBlock.endTime) && (
                  <Text size="xs" c="dimmed">
                    Time:{" "}
                    {formatTimeRange(
                      dayjs(contentBlock.startTime).format("hh:mm A"),
                      dayjs(contentBlock.endTime).format("hh:mm A")
                    )}
                  </Text>
                )}
                {contentBlock.recurrencePattern &&
                  contentBlock.recurrencePattern !== "NONE" && (
                    <Text size="xs" c="dimmed">
                      {contentBlock.recurrencePattern}
                      {contentBlock.recurrenceDays &&
                        `: ${formatDaysOfWeek(contentBlock.recurrenceDays)}`}
                    </Text>
                  )}
              </Stack>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Displays List */}
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Select Displays
          </Text>

          {loading ? (
            <Group justify="center" p="xl">
              <LoadingIndicator size={32} />
              <Text size="sm" c="dimmed">
                Loading displays...
              </Text>
            </Group>
          ) : displays.length === 0 ? (
            <Alert
              icon={<AppIcon iconName="InfoCircle" size={16} />}
              title="No displays found"
              color="blue"
            >
              <Text size="sm">
                No displays are available. Create a display first to assign
                emergency content blocks.
              </Text>
            </Alert>
          ) : (
            <ScrollArea.Autosize mah={400}>
              <Stack gap="xs">
                {displays.map((display) => {
                  if (!display) {
                    return null;
                  }

                  const isAssigned = assignedDisplayIds.has(display.id);
                  const operation = operations[display.id];
                  const isOperationLoading = operation?.loading || false;
                  const operationError = operation?.error || null;

                  return (
                    <Box key={display.id}>
                      <Group gap="sm" wrap="nowrap" p="xs" align="flex-start">
                        {isOperationLoading ? (
                          <LoadingIndicator size={18} />
                        ) : (
                          <Checkbox
                            checked={isAssigned}
                            onChange={(e) =>
                              handleCheckboxChange(
                                display.id,
                                e.currentTarget.checked
                              )
                            }
                            disabled={isOperationLoading}
                            size="sm"
                            mt={3}
                          />
                        )}

                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="md" fw={600} truncate>
                            {display.name || "Untitled Display"}
                          </Text>
                          {display.description && (
                            <Text size="xs" c="dimmed" truncate>
                              {display.description}
                            </Text>
                          )}
                          {display.venue?.name && (
                            <Text size="xs" c="dimmed" truncate>
                              Venue: {display.venue.name}
                            </Text>
                          )}
                        </Box>

                        {isAssigned && !isOperationLoading && (
                          <AppIcon iconName="Check" size={16} color="green" />
                        )}
                      </Group>

                      {operationError && (
                        <Alert
                          icon={<AppIcon iconName="AlertCircle" size={16} />}
                          color="red"
                          mt={4}
                          withCloseButton
                          onClose={() => clearDisplayError(display.id)}
                        >
                          <Text size="xs">{operationError}</Text>
                        </Alert>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </ScrollArea.Autosize>
          )}
        </Stack>

        {/* Summary */}
        {displays.length > 0 && !loading && (
          <Group gap="xs">
            <AppIcon iconName="InfoCircle" size={16} />
            <Text size="xs" c="dimmed">
              {assignedDisplayIds.size} of {displays.length} displays selected
            </Text>
          </Group>
        )}
      </Stack>
    </AppModal>
  );
};
